import { useTranslation } from "react-i18next";
import {
  computeProposalTotals,
  proposalChecklist,
  type ProposalDto,
  type TripDto,
} from "@tc/shared";
import { Card, CardTitle, CostTotal, Icon, OptionList, OptionRow, TRANSPORT_ICON } from "@/components/ui";
import { useFormatters } from "@/lib/i18n/format";
import { UnselectedOptionsCard } from "@/features/proposal/UnselectedOptionsCard";
import { TRANSPORT_LABEL } from "./RequestSummaryCard";

/**
 * The stage of the back-office STATE 2 (read-only, tabbed) trip view. Drives the Overview banner
 * and the left card title/total semantics. `sent` = proposal with the club, awaiting a decision;
 * `accepted` = the club confirmed their selection — the agreed state, and the neutral fallback for
 * the remaining non-completed terminal states (Cancelled/Postponed); `expired` = the club let the
 * acceptance deadline lapse without choosing — no selection exists, so it reads like `sent` (open
 * options, none chosen) but with an expired banner and a Revise CTA (TRAV-64); `completed` = the
 * trip has finished and is closed out — the same agreed layout, but a completed banner and a locked
 * roster.
 */
export type BoProposalVariant = "sent" | "accepted" | "completed" | "expired";

/**
 * The back-office STATE 2 status banner — amber while awaiting the club, green once the club has
 * confirmed (accepted) or the trip has wrapped (completed), red once the acceptance deadline has
 * lapsed (expired). Rendered at the top of the Overview; also stands alone above the generic
 * overview for a Completed trip that has no proposal (the historical invoice-import trips), where
 * `version` is omitted and the copy drops the "Version N" lead-in.
 */
export function BoStateBanner({
  variant,
  version,
  draftVersion,
}: {
  variant: BoProposalVariant;
  version?: number | null;
  /** An unsent Draft revision exists (UAT round 3) — `version` is then the version the CLUB is on,
   *  and the banner adds a note that the draft stays invisible to them until sent. */
  draftVersion?: number | null;
}) {
  const { t } = useTranslation();
  const hasVersion = version != null;
  const banner =
    variant === "sent"
      ? {
          tone: "bo-banner-sent",
          icon: "send" as const,
          title: t("proposal.bannerSentTitle"),
          body: t("proposal.bannerSentBody", { version }),
          label: t("proposal.bannerSentLabel"),
        }
      : variant === "expired"
        ? {
            tone: "bo-banner-expired",
            icon: "clock" as const,
            title: t("proposal.bannerExpiredTitle"),
            body: t("proposal.bannerExpiredBody", { version }),
            label: t("proposal.bannerExpiredLabel"),
          }
        : variant === "completed"
          ? {
              tone: "bo-banner-accepted",
              icon: "checkCircle" as const,
              title: t("proposal.bannerCompletedTitle"),
              body: hasVersion
                ? t("proposal.bannerCompletedBody", { version })
                : t("proposal.bannerCompletedBodyPlain"),
              label: t("proposal.bannerCompletedLabel"),
            }
          : {
              tone: "bo-banner-accepted",
              icon: "checkCircle" as const,
              title: t("proposal.bannerAcceptedTitle"),
              body: t("proposal.bannerAcceptedBody", { version }),
              label: t("proposal.bannerAcceptedLabel"),
            };
  return (
    <div className={`bo-banner ${banner.tone}`}>
      <Icon name={banner.icon} size={20} className="bo-banner-ic" />
      <div className="bo-banner-body">
        <h4>{banner.title}</h4>
        <p>
          {banner.body}
          {draftVersion != null && <> {t("proposal.bannerRevisionNote", { draftVersion })}</>}
        </p>
      </div>
      <span className="bo-banner-label">{banner.label}</span>
    </div>
  );
}

/** Whole nights between the trip's travel dates (0 if either is missing or it's a day trip). */
function nightsOf(trip: TripDto): number {
  if (!trip.travelStartDate || !trip.travelEndDate) return 0;
  const ms = new Date(trip.travelEndDate).getTime() - new Date(trip.travelStartDate).getTime();
  return Math.max(0, Math.round(ms / 86_400_000));
}

/** Whole days from today until an ISO date (negative once past) — drives the deadline tone/label. */
function daysUntil(iso: string): number {
  const due = new Date(iso);
  due.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86_400_000);
}

/**
 * Back-office STATE 2 Overview (TW proposal lifecycle). The read-only counterpart to the club's
 * `ClubProposalView` — same `.prop-grid` layout, but no accept/select UI: TC is watching the
 * proposal it sent, or reviewing the club's accepted selection before confirming the trip.
 *
 * The proposal is passed in from the page (loaded alongside the trip), so this fetches nothing.
 *
 * Declined lines (TRAV-66): on accept the unchosen lines are kept (flagged `selected = false`) and
 * returned as `proposal.unselectedLineItems`, so the accepted view badges the kept (selected) lines
 * "CHOSEN BY CLUB" and lists the declined options in a read-only card below.
 */
export function TripOverviewBo({
  trip,
  proposal,
  variant,
}: {
  trip: TripDto;
  proposal: ProposalDto;
  variant: BoProposalVariant;
}) {
  const { t } = useTranslation();
  const fmt = useFormatters();

  // The accepted *and* completed views show the club's confirmed selection (the chosen lines). A
  // still-sent proposal — and an expired one, where the club never chose (TRAV-64) — frames it as
  // the open set of "Trip options" with nothing marked chosen.
  const showSelection = variant === "accepted" || variant === "completed";
  const totals = computeProposalTotals(
    proposal.lineItems.map((l) => ({
      qty: l.qty,
      unitCost: l.unitCost,
      marginType: l.marginType,
      marginValue: l.marginValue,
    })),
    trip.paxCount,
  );
  const pax = trip.paxCount ?? 0;
  const nights = nightsOf(trip);
  const items = proposalChecklist(proposal, trip.transportModes, {
    manifestSubmitted: trip.manifestSubmittedAt != null,
    tripCompleted: trip.status === "Completed",
  });

  const primaryTransport = (["flight", "train", "coach", "hireCar"] as const).find(
    (m) => trip.transportModes?.[m],
  );

  // The latest version being an (unsent) Draft means the club is on the version before it — name
  // THAT version in the banner/summary and flag the draft, instead of decorating the draft with the
  // sent/accepted state (it once read "Version 2 is with the club" while the club saw v1).
  const isDraftRevision = proposal.status === "Draft";
  const liveVersion = isDraftRevision && proposal.version > 1 ? proposal.version - 1 : proposal.version;

  return (
    <>
      <BoStateBanner
        variant={variant}
        version={liveVersion}
        draftVersion={isDraftRevision ? proposal.version : null}
      />

      <div className="prop-grid">
        <div className="stack" style={{ gap: "var(--gap)" }}>
        <Card>
          <div className="row-between" style={{ alignItems: "center" }}>
            <CardTitle>{showSelection ? t("proposal.clubSelection") : t("proposal.tripOptions")}</CardTitle>
            {proposal.sentAt && (
              <span className="sub" style={{ fontSize: 12.5 }}>
                {t("proposal.sentDate", { date: fmt.date(proposal.sentAt) })}
              </span>
            )}
          </div>

          <OptionList style={{ marginTop: 14 }}>
            {proposal.lineItems.map((line) => (
              <OptionRow
                key={line.id}
                description={line.description}
                qtyLabel={`${line.qty} × ${fmt.currency(line.unitPrice, proposal.currency)}`}
                amountLabel={fmt.currency(line.amount, proposal.currency)}
                trailing={
                  showSelection ? (
                    <span className="badge-chosen">{t("proposal.chosenByClub")}</span>
                  ) : undefined
                }
              />
            ))}
          </OptionList>

          <CostTotal
            label={t("proposal.total")}
            perPersonLabel={`${fmt.currency(totals.perPerson, proposal.currency)} ${t("proposal.perPerson")} · ${pax} ${t("units.pax")}${
              nights > 0 ? ` · ${nights} ${t(nights === 1 ? "units.night" : "units.nights")}` : ""
            }`}
            amountLabel={fmt.currency(totals.total, proposal.currency)}
          />

          {primaryTransport && (
            <div>
              <span className="transport-chip">
                <Icon name={TRANSPORT_ICON[primaryTransport]} size={14} />
                {t(TRANSPORT_LABEL[primaryTransport])}
              </span>
            </div>
          )}
        </Card>

        <UnselectedOptionsCard lines={proposal.unselectedLineItems} currency={proposal.currency} />
        </div>

        <aside className="summary-aside">
          <Card>
            <CardTitle>{t("proposal.deadlines")}</CardTitle>
            {items.length === 0 ? (
              <div className="sub" style={{ marginTop: 10 }}>
                —
              </div>
            ) : (
              <div className="dl-list" style={{ marginTop: 14 }}>
                {items.map((dl) => {
                  const overdue = !dl.done && daysUntil(dl.date) < 0;
                  return (
                    <div className="dl-list-row" key={dl.key}>
                      <span className={`dl-icon ${dl.done ? "done" : overdue ? "overdue" : ""}`}>
                        {dl.done ? (
                          <Icon name="check" size={14} stroke={2.5} />
                        ) : (
                          <Icon name="clock" size={15} />
                        )}
                      </span>
                      <span className="dl-label">{dl.label ?? (dl.labelKey ? t(dl.labelKey) : "")}</span>
                      <span className={`dl-status ${overdue ? "overdue" : ""}`}>
                        {dl.done ? t("proposal.deadlineDone") : fmt.relative(dl.date)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </aside>
      </div>
    </>
  );
}
